import { useState, type ReactNode, type SVGProps } from "react";
import { NavLink, useLocation, type Location } from "react-router-dom";
import {
  MODULES_CATALOG,
  CATEGORY_LABELS,
  CATEGORY_SHORT,
  isModuleEnabled,
  isModuleVisible,
  type ModuleCategory,
} from "@nxvolt/shared";
import { useAuth } from "../lib/auth-context.js";
import {
  NAV_BADGES_VAZIO,
  SEM_PENDENCIA,
  combinarContagens,
  type ContagemNav,
  type NavBadges,
} from "../lib/use-nav-badges.js";
import {
  ICON_BY_SLUG,
  IconGeneric,
  IconChevron,
  IconChevronsLeft,
  IconChevronsRight,
  IconDashboard,
  IconChargers,
  IconLive,
  IconBolt,
  IconTruck,
  IconMoney,
  IconSettings,
} from "./icons.js";

interface SidebarProps {
  /** Ativa modo drawer (mobile). */
  drawer?: boolean;
  /** Estado atual do drawer, controla a classe [data-open]. */
  open?: boolean;
  /** Callback ao clicar num item de navegação — usado para fechar o drawer. */
  onNavigate?: () => void;
  /**
   * Sidebar colapsada (só o trilho de categorias, sem o painel). Ignorado no
   * modo drawer (mobile) porque lá o "colapsar" não faz sentido — se o drawer
   * está fechado, não se vê nem o trilho.
   */
  collapsed?: boolean;
  /** Toggle do estado colapsado. Botão só é renderizado se este callback existir. */
  onToggleCollapsed?: () => void;
  /**
   * Contadores de pendência vindos do `AppShell`. Não é o Sidebar que busca:
   * o shell monta duas instâncias (desktop + drawer) e o polling precisa
   * acontecer uma vez só, com as duas enxergando o mesmo resultado.
   */
  badges?: NavBadges;
}

/** Ícone que representa a categoria no trilho. Reaproveita o acervo de
 *  `icons.tsx` — o ícone é o do assunto do grupo, não o do primeiro item,
 *  que mudaria de desenho a cada reordenação do catálogo. */
const ICON_BY_CATEGORY: Record<ModuleCategory, (p: SVGProps<SVGSVGElement>) => ReactNode> = {
  principal: IconDashboard,
  carregadores: IconChargers,
  operacao: IconLive,
  gestao_eletrica: IconBolt,
  frota: IconTruck,
  financeiro: IconMoney,
  admin: IconSettings,
};

/** Ordem canônica das categorias no trilho. Mesma ordem do catálogo. */
const ORDEM_CATEGORIAS: ModuleCategory[] = [
  "principal",
  "carregadores",
  "operacao",
  "gestao_eletrica",
  "frota",
  "financeiro",
  "admin",
];

/**
 * Como uma contagem vira pixels e texto. Um lugar só, usado pelo item do
 * painel e pelo botão do trilho, porque os dois têm de contar a MESMA
 * história — no colapsado o ponto é o único sinal, e ele não pode dizer
 * "nada pendente" quando o que houve foi falha de leitura.
 *
 * | estado     |  n  | ponto/badge                   | leitor de tela          |
 * |------------|-----|-------------------------------|-------------------------|
 * | carregando |  –  | nada                          | só o nome               |
 * | ok         |  0  | nada                          | só o nome               |
 * | ok         | >0  | vermelho, com o número        | "Alertas, 21 em aberto" |
 * | sem-sinal  |  n  | aviso, apagado, "n" + "(últ.)"| "..., sem sinal, último: 21 em aberto" |
 * | sem-sinal  | –   | aviso, apagado, "?"           | "Alertas, sem sinal"    |
 */
interface Aparencia {
  /** Desenha ponto (trilho) / badge (painel)? */
  marca: boolean;
  /** Degradação STALE: o valor não é leitura viva. */
  semSinal: boolean;
  /** Conteúdo do badge do painel. */
  texto: string;
  /**
   * Nome acessível completo do controle. **Nunca `undefined`.**
   *
   * Era opcional — "o nome cru já basta" — e no painel de fato bastava,
   * porque lá o texto visível é o nome inteiro do módulo. No trilho não:
   * o texto visível é `CATEGORY_SHORT` ("Energ.", "Fin.", "Oper."), o
   * `aria-label` saía omitido e o nome acessível caía para a abreviação.
   * `title` não resolve: pelo cálculo do nome acessível, conteúdo de texto
   * vence `title`, que sobra como *description*.
   */
  rotulo: string;
  /** Descrição no `title` — também cobre o nome truncado por ellipsis. */
  titulo: string;
}

function aparenciaDe(nome: string, c: ContagemNav): Aparencia {
  if (c.estado === "sem-sinal") {
    const rotulo =
      c.n === null
        ? `${nome}, sem sinal`
        : `${nome}, sem sinal, último: ${c.n} em aberto`;
    return {
      marca: true,
      semSinal: true,
      // "?" e não "0": o badge sem sinal não tem número para mostrar.
      texto: c.n === null ? "?" : c.n > 99 ? "99+" : String(c.n),
      rotulo,
      titulo:
        c.n === null
          ? `${nome} — sem sinal, contagem desconhecida`
          : `${nome} — sem sinal, último valor recebido: ${c.n} em aberto`,
    };
  }
  const n = c.estado === "ok" ? (c.n ?? 0) : 0;
  if (n > 0) {
    return {
      marca: true,
      semSinal: false,
      texto: n > 99 ? "99+" : String(n),
      // Leitor de tela precisa do número junto do nome: ler
      // "Alertas" e depois "21" solto não diz nada.
      rotulo: `${nome}, ${n} em aberto`,
      titulo: nome,
    };
  }
  // Sem pendência o rótulo é o nome completo — e não `undefined`. No painel
  // é igual ao texto visível (nome acessível inalterado); no trilho é o que
  // impede o nome de cair para "Energ." / "Fin.". Ver spec §4.
  return { marca: false, semSinal: false, texto: "", rotulo: nome, titulo: nome };
}

/** Slug do módulo a partir do pathname. O router usa hífen na URL
 *  (`/app/saude-bateria`) e underscore no slug do catálogo
 *  (`saude_bateria`) — a mesma normalização que o `ModuleRoute` faz.
 *  `/app/chargers/:cpId` também resolve para `chargers`. */
function slugDaRota(pathname: string): string {
  const seg = pathname.split("/").filter(Boolean); // ["app", "<slug>", ...]
  const bruto = seg[0] === "app" ? (seg[1] ?? "") : "";
  if (bruto === "saude-bateria") return "saude_bateria";
  if (bruto === "battery-health" || bruto === "battery_health") return "saude_bateria";
  return bruto;
}

export function Sidebar({
  drawer = false,
  open = false,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
  badges = NAV_BADGES_VAZIO,
}: SidebarProps = {}) {
  const { user } = useAuth();
  const location = useLocation();
  // Grupo escolhido por clique, junto do OBJETO `location` vigente no
  // clique. Estado efêmero de propósito: NÃO vai para localStorage. Um grupo
  // aberto persistido brigaria com a rota — quem fechou "Operação" e
  // recarregou em /app/alerts veria o menu sem o item onde está.
  const [selecao, setSelecao] = useState<{
    cats: ReadonlySet<ModuleCategory>;
    loc: Location;
  } | null>(null);
  if (!user) return null;

  /** Pendências que merecem número no menu. Slug fora daqui não ganha
   *  badge — contador em tudo deixa de sinalizar qualquer coisa. Note que
   *  o default é `SEM_PENDENCIA` (zero AFIRMADO), não "desconhecido": para
   *  Dashboard ou Configurações não há nada a contar, e isso é certeza. */
  const contadorDe = (slug: string): ContagemNav => {
    if (slug === "alerts") return badges.alerts;
    if (slug === "maintenance") return badges.maintenance;
    return SEM_PENDENCIA;
  };

  const allowed = new Set(user.modules);
  const visible = MODULES_CATALOG.filter(
    (m) =>
      isModuleEnabled(m.slug) &&
      isModuleVisible(m.slug) &&
      !(m.slug === "cco" && user.garageScope === "restricted") &&
      (user.role === "admin" || allowed.has(m.slug)),
  ).sort((a, b) => a.order - b.order);

  const byCategory = visible.reduce<Record<ModuleCategory, typeof visible>>(
    (acc, m) => {
      (acc[m.category] ||= [] as typeof visible).push(m);
      return acc;
    },
    {
      principal: [],
      carregadores: [],
      operacao: [],
      gestao_eletrica: [],
      frota: [],
      financeiro: [],
      admin: [],
    },
  );

  // Categoria só existe no menu se sobrou item depois do filtro de permissão.
  const categorias = ORDEM_CATEGORIAS.filter((c) => byCategory[c].length > 0);

  // No modo drawer (mobile) o colapso não se aplica — o drawer é
  // "aberto/fechado", não "expandido/colapsado".
  const isCollapsed = collapsed && !drawer;

  const cls =
    (drawer ? "nx-sidebar nx-sidebar--drawer" : "nx-sidebar") +
    (isCollapsed ? " nx-sidebar--collapsed" : "");

  // Categoria a que a rota atual pertence DE FATO, ou null quando o slug não
  // é de nenhum módulo do menu. Há rotas assim alcançáveis pela UI: o link
  // "Trocar senha" da Topbar leva a /app/change-password, que o `ModuleRoute`
  // atende sem redirecionar (AppShell.tsx) e que não existe no catálogo.
  // Esta é a única fonte de `aria-current` — ver o comentário no botão.
  const slug = slugDaRota(location.pathname);
  const catDaRota =
    categorias.find((c) => byCategory[c].some((m) => m.slug === slug)) ?? null;
  // Grupo aberto por padrão. Aí o fallback é legítimo: um painel vazio não
  // ajudaria ninguém, e "qual grupo estou vendo" é pergunta diferente de
  // "onde eu estou".
  const catAbertaPadrao = catDaRota ?? categorias[0] ?? null;
  // A seleção manual só vale enquanto a navegação não mudou: navegar reabre o
  // grupo do módulo em que a pessoa está, que é a fonte de verdade.
  //
  // A comparação é por IDENTIDADE do objeto `location`, e não por pathname
  // nem por `location.key`:
  //  - pathname não é identidade de navegação. /app/alerts -> clique em
  //    "Frota" -> /app/fleet -> Voltar do navegador volta ao MESMO pathname,
  //    e o painel ressuscitava "Frota" com a tela exibindo Alertas.
  //  - `location.key` tampouco resolve: o Voltar restaura a entrada de
  //    histórico original, com a MESMA key (reproduzido: a entrada inicial
  //    volta como `default`). O bug sobrevivia igual.
  // O router cria um objeto `location` novo a cada navegação (PUSH, REPLACE
  // e POP) e o mantém estável entre re-renders, então identidade responde
  // exatamente à pergunta "houve navegação desde o clique?".
  //
  // O modelo mudou de "uma categoria aberta" para "um CONJUNTO", que e o do
  // template de referencia: numa coluna unica da para ver varios grupos ao
  // mesmo tempo, e forcar um-de-cada-vez faria o menu fechar o grupo que a
  // pessoa acabou de abrir so porque ela abriu outro.
  const abertos: ReadonlySet<ModuleCategory> =
    selecao && selecao.loc === location
      ? selecao.cats
      : new Set(catAbertaPadrao ? [catAbertaPadrao] : []);

  /** Abre/fecha um grupo, preservando os demais. */
  const alternar = (cat: ModuleCategory) => {
    const proximo = new Set(abertos);
    if (proximo.has(cat)) proximo.delete(cat);
    else proximo.add(cat);
    setSelecao({ cats: proximo, loc: location });
  };

  /** Pendências agregadas da categoria — vira o ponto no trilho. Não é uma
   *  soma simples: se um dos itens está sem sinal, o total da categoria é
   *  desconhecido (ver `combinarContagens`). */
  const pendenciasDe = (cat: ModuleCategory): ContagemNav =>
    combinarContagens(byCategory[cat].map((m) => contadorDe(m.slug)));

  /** Lista de módulos de uma categoria, já com badge e rótulo acessível. */
  const renderLista = (cat: ModuleCategory) => (
    <ul className="nx-rail__list">
      {byCategory[cat].map((m) => {
        // Ícone SVG por slug. Emoji do catalog fica só como fallback
        // de última hora se um módulo novo aparecer antes do mapa.
        const Icon = ICON_BY_SLUG[m.slug] ?? IconGeneric;
        const ap = aparenciaDe(m.name, contadorDe(m.slug));
        return (
          <li key={m.slug}>
            <NavLink
              to={`/app/${m.slug}`}
              onClick={onNavigate}
              // Sem tabIndex quando fechado: quem navega por teclado
              // não fica pulando itens escondidos.
              tabIndex={drawer && !open ? -1 : undefined}
              aria-label={ap.rotulo}
              // O rótulo é truncado por ellipsis quando não cabe nos 232px
              // do painel ("Aprovação de Carregadores"). O `title` devolve o
              // nome inteiro a quem usa mouse; o nome acessível nunca foi
              // truncado, porque o texto continua completo no DOM.
              title={ap.titulo}
              className={({ isActive }) =>
                "nx-rail__link" +
                (isActive ? " nx-rail__link--active" : "") +
                (ap.marca && !ap.semSinal ? " nx-rail__link--pendente" : "") +
                (ap.semSinal ? " nx-rail__link--sem-sinal" : "")
              }
            >
              <Icon />
              <span className="nx-rail__label">{m.name}</span>
              {ap.marca && (
                <span
                  className={
                    "nx-rail__badge" + (ap.semSinal ? " nx-rail__badge--sem-sinal" : "")
                  }
                  aria-hidden="true"
                >
                  {ap.texto}
                </span>
              )}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );

  // Mobile: lista única. O trilho não vem junto — 76px de trilho + 232px de
  // painel dentro de uma tela de 390px deixaria o conteúdo sem lugar.
  if (drawer) {
    return (
      <nav
        id="nx-sidebar-drawer"
        className={cls}
        aria-label="Menu principal"
        data-open={open ? "true" : "false"}
        // Quando fechado, remove do fluxo de foco por teclado. Ainda existe
        // no DOM pra o transform funcionar sem re-mount.
        aria-hidden={!open ? true : undefined}
      >
        <div className="nx-rail__panel">
          {categorias.map((cat) => (
            <section key={cat} className="nx-rail__group">
              <h2 className="nx-rail__title">{CATEGORY_LABELS[cat]}</h2>
              {renderLista(cat)}
            </section>
          ))}
        </div>
      </nav>
    );
  }

  // O AppShell monta duas instâncias do Sidebar (desktop + drawer). Estes ids
  // só são emitidos no ramo desktop — o drawer retorna acima, sem disclosure
  // e sem aria-controls —, então não há id duplicado no documento.
  const idLista = (cat: ModuleCategory) => `nx-menu-lista-${cat}`;

  return (
    <nav className={cls} aria-label="Menu principal">
      <div className="nx-rail">
        {onToggleCollapsed && (
          <button
            type="button"
            className="nx-rail__collapse"
            onClick={onToggleCollapsed}
            aria-label={isCollapsed ? "Expandir menu" : "Recolher menu"}
            aria-expanded={!isCollapsed}
            title={isCollapsed ? "Expandir menu" : "Recolher menu"}
          >
            {isCollapsed ? <IconChevronsRight /> : <IconChevronsLeft />}
          </button>
        )}

        {categorias.map((cat) => {
          const Icon = ICON_BY_CATEGORY[cat];
          const ap = aparenciaDe(CATEGORY_LABELS[cat], pendenciasDe(cat));
          // Colapsado nao ha lista para expandir: o clique no icone expande a
          // barra inteira (ver onClick), entao `aria-expanded` seria mentira.
          const estaAberto = abertos.has(cat) && !isCollapsed;
          return (
            <div key={cat} className="nx-rail__group">
              <button
                type="button"
                className={
                  "nx-rail__btn" + (estaAberto ? " nx-rail__btn--active" : "")
                }
                // Disclosure, não tab: o que abre é uma lista de links de
                // navegação, não um painel de conteúdo. role=tab prometeria ao
                // leitor de tela navegação por setas, que não é o que se
                // espera de um menu.
                aria-expanded={estaAberto}
                aria-controls={isCollapsed ? undefined : idLista(cat)}
                aria-label={ap.rotulo}
                // Três coisas diferentes, três atributos diferentes:
                //   aria-current  = onde a pessoa ESTÁ (grupo da rota)
                //   aria-expanded = se este grupo está aberto
                //   o destaque    = idem
                // `aria-current` continua vindo de `catDaRota`, NUNCA de
                // `abertos`: abrir um grupo não é navegar. `catDaRota` é null
                // quando a rota não é de módulo nenhum (/app/change-password),
                // e aí nenhum botão recebe a marca — anunciar "você está em
                // Início" enquanto a tela é Trocar Senha é pior que calar.
                aria-current={catDaRota === cat ? true : undefined}
                title={ap.titulo}
                onClick={() => {
                  // Colapsado, o clique tem outro significado: expandir a
                  // barra. Abrir um grupo que ninguém pode ver não teria
                  // efeito visível nenhum.
                  if (isCollapsed) {
                    if (onToggleCollapsed) onToggleCollapsed();
                    if (!abertos.has(cat)) alternar(cat);
                    return;
                  }
                  alternar(cat);
                }}
              >
                <Icon />
                {/* Duas grafias do mesmo nome, e a escolha é do CSS: expandido
                    mostra o nome inteiro, colapsado mostra a abreviação. As
                    duas ficam no DOM porque alternar o TEXTO por JS quebraria
                    o nome acessível durante a transição de largura. */}
                <span className="nx-rail__label">{CATEGORY_LABELS[cat]}</span>
                <span className="nx-rail__short">{CATEGORY_SHORT[cat]}</span>
                {/* O ponto é aria-hidden: a informação já está no aria-label.
                    Aparece em dois casos bem diferentes — pendência viva
                    (vermelho, cheio) e sem sinal (aviso, apagado). O que ele
                    NUNCA faz é desaparecer por falha de leitura: com o menu
                    colapsado este ponto é o único sinal de pendência da tela
                    inteira, e apagá-lo afirmaria "nada pendente". */}
                {ap.marca && (
                  <span
                    className={
                      "nx-rail__dot" + (ap.semSinal ? " nx-rail__dot--sem-sinal" : "")
                    }
                    aria-hidden="true"
                  />
                )}
                <IconChevron className="nx-rail__chevron" aria-hidden="true" />
              </button>

              {/* A lista some do DOM quando fechada, em vez de ficar com
                  `display: none`. Com 7 grupos e ~30 módulos, deixar tudo
                  montado custaria 30 NavLink recalculando `isActive` a cada
                  navegação — e um `tabIndex={-1}` em cada um para o teclado
                  não passear por item invisível. */}
              {estaAberto && (
                <div id={idLista(cat)} className="nx-rail__sub">
                  {renderLista(cat)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
